import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { IFile } from '../../model/file.model';
import { ISibasiHTTPResponse } from '../../model/sibasiHTTPResponse.model';
import { IUser, UserRoles, IAuthItem } from '../../model/user.model';
import { IUserRole } from '../../model/userRoles.model';
import { IPagination } from '../../model/util.model';
import { IMember } from '../../model/members.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(private httpClient: HttpClient) {}

  getAllUsers(organizationId: string): Observable<ISibasiHTTPResponse<IUser[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUser[]>>(
      `${environment.api_url}user/admin/${organizationId}`
    );
  }

  createUser(user: {}): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/admin`,
      user
    );
  }

  updateUser(user: {}, userId: string): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/admin/updateUser/${userId}`,
      user
    );
  }

  // deleteUser(userId: string): Observable<ISibasiHTTPResponse<IUser>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IUser>>(`${environment.api_url}user/admin/${userId}/deleteUser`);
  // }

  deleteUser(userId: string): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/slateforDeletion/${userId}`
    );
  }

  restoreDeletedUser(userId: string): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/unslateforDeletion/${userId}`
    );
  }

  getDeletedUsers(): Observable<ISibasiHTTPResponse<IUser[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUser[]>>(
      `${environment.api_url}user/admin/allDeleted`
    );
  }

  getUserById(userId: string): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/admin/getUser/${userId}`
    );
  }

  assignUserRole(
    role: { role: UserRoles },
    userId: string
  ): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/admin/assignRole/${userId}`,
      role
    );
  }

  unAssignUserRole(
    role: { role: UserRoles },
    userId: string
  ): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/admin/removeRole/${userId}`,
      role
    );
  }

  getAllUsersCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}user/admin/all/count`
    );
  }
  getAllSearchedUsersCount(searchParam: string): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}user/admin/all/searched`,
      { searchParam }
    );
  }
  getPaginatedUsers(pagination?: any): Observable<ISibasiHTTPResponse<IUser[]>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUser[]>>(
      `${environment.api_url}user/admin/all/paginated`,
      pagination ? pagination : {}
    );
  }
  getUnApprovedUsers(pagination?: any): Observable<ISibasiHTTPResponse<IUser[]>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUser[]>>(
      `${environment.api_url}user/unApprovedUsers`,
      pagination ? pagination : {}
    );
  }
  unApprovedUserCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}user/unApprovedUserCount`
    );
  }
  unApprovedSearchedUserCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}user/unApprovedSearchedUserCount`,
      { searchParam }
    );
  }
  getApprovedUsers(pagination?: any): Observable<ISibasiHTTPResponse<IUser[]>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUser[]>>(
      `${environment.api_url}user/approvedUsers`,
      pagination ? pagination : {}
    );
  }
  approvedUserCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}user/approvedUserCount`
    );
  }
  sendVerificationEmail(
    userId: string,
    authItemValue: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}user/admin/sendVerificationEmail/${userId}`,
      { email: authItemValue }
    );
  }
  approvedSearchedUserCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}user/approvedSearchedUserCount`,
      { searchParam }
    );
  }

  getAllUserRoles(): Observable<ISibasiHTTPResponse<IUserRole[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUserRole[]>>(
      `${environment.api_url}userRole/all`
    );
  }

  getAllHardCodedUserRoles(): Observable<ISibasiHTTPResponse<string[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<string[]>>(
      `${environment.api_url}user/getAvailableSystemRoles`
    );
  }

  createUserRole(userRole: IUserRole): Observable<ISibasiHTTPResponse<IUserRole>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUserRole>>(
      `${environment.api_url}userRole`,
      userRole
    );
  }

  updateUserRole(
    userRole: IUserRole,
    userRoleId: string
  ): Observable<ISibasiHTTPResponse<IUserRole>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUserRole>>(
      `${environment.api_url}userRole/update/${userRoleId}`,
      userRole
    );
  }

  deleteUserRole(userRoleId: string): Observable<ISibasiHTTPResponse<IUserRole>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IUserRole>>(
      `${environment.api_url}userRole/delete/${userRoleId}`
    );
  }

  getUserRoleById(userRoleId: string): Observable<ISibasiHTTPResponse<IUserRole>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUserRole>>(
      `${environment.api_url}userRole/${userRoleId}`
    );
  }

  getUserProfile(): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/profile`
    );
  }

  getMemberById(memberId: string): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.get<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organization/members/one/${memberId}`
    );
  }

  getOrgMemberRole(): Observable<ISibasiHTTPResponse<string>> {
    return this.httpClient.get<ISibasiHTTPResponse<string>>(
      `${environment.api_url}organization/member/role`
    );
  }

  updateUserProfile(user: {}): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.post<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/profile`,
      user
    );
  }

  getProfilePictureMetaData(): Observable<ISibasiHTTPResponse<IFile>> {
    return this.httpClient.get<ISibasiHTTPResponse<IFile>>(
      `${environment.api_url}user/profilePicture`
    );
  }

  addUserAuthItem(authItem: {}): Observable<ISibasiHTTPResponse<IAuthItem>> {
    return this.httpClient.post<ISibasiHTTPResponse<IAuthItem>>(
      `${environment.api_url}user/addAuthItem`,
      authItem
    );
  }

  deleteUserAuthItem(authItem: IAuthItem): Observable<ISibasiHTTPResponse<IAuthItem>> {
    return this.httpClient.post<ISibasiHTTPResponse<IAuthItem>>(
      `${environment.api_url}user/removeAuthItem`,
      authItem
    );
  }

  /* ############################# NEW SERVICES ############################# */

  getAllUsersPagination(
    paginationParams: IPagination<IUser[]>
  ): Observable<ISibasiHTTPResponse<IPagination<IUser[]>>> {
    return this.httpClient.post<ISibasiHTTPResponse<IPagination<IUser[]>>>(
      `${environment.api_url}user/admin/all/paginated`,
      paginationParams
    );
  }

  deleteAccount(): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/account/mine`
    );
  }
  getAllUsersAdmin(): Observable<ISibasiHTTPResponse<IUser[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUser[]>>(
      `${environment.api_url}user/admin`
    );
  }

  addUserReferralCode(referralCode: string): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}user/referral/update/referredBy/${referralCode}`
    );
  }
}
